import { useEffect, useRef, useState } from "react";
import { lei } from "@/shared/format";
import type { CartLine } from "./cart";

import type { HouseRules } from "./App";

type Props = {
  /** The house's own rules, from the snapshot the menu already fetched. */
  rules: HouseRules | null;
  eventSlug: string;
  zone: string | null;
  /**
   * The house's own word for that zone — "Pergola 3", "Foc de tabără" — out of
   * the same snapshot the rules came from. Null when the menu has not loaded
   * yet, or when the scanned code names no zone the house still has.
   */
  zoneLabel: string | null;
  lines: CartLine[];
  subtotal: number;
  idempotencyKey: string;
  lang: string;
  onQty: (menuItemId: string, qty: number) => void;
  onClose: () => void;
  /*
   * `method` rides along because MIA and card want opposite things from the
   * pay URL. A card page redirects the guest back to us when it is done; a MIA
   * QR cannot — its URL is the QR's own payload, scanned by a banking app that
   * has nowhere to return anyone to. So the caller has to know which it got.
   */
  onPlaced: (orderId: string, payUrl: string | null, method: string) => void;
  /**
   * MOD BAR — staff typing for a guest who cannot scan a QR.
   *
   * It changes THREE things on this screen and nothing else. The method is
   * cash and the picker is gone (there is no card terminal in this flow and
   * nobody to redirect to a bank). Nothing is asked about the guest — no
   * address, no phone, no acceptance box — because the person at the keyboard
   * is not the person buying, and one person cannot consent on another's
   * behalf. And the draft, along with every remembered habit, is left alone:
   * they are one guest's, and a counter serves fifty.
   *
   * The flag is a REQUEST, not a permission. worker/api/orders.ts reads the
   * staff cookie and refuses everything that cannot prove it — see `barActor`.
   */
  bar?: boolean;
  /**
   * Where a counter order lands instead of `onPlaced`: the code to read aloud
   * and the cash to collect, with none of the localStorage crumbs a guest's
   * own order leaves behind on their own phone.
   */
  onBarPlaced?: (pickupCode: string, totalMinor: number) => void;
};

const T = {
  ro: {
    title: "Comanda ta", empty: "Coșul e gol.",
    // The WHOLE question, not just its first half: the name is for the moment
    // the tray is ready and somebody shouts it — saying so is what makes a
    // guest give a callable name instead of wondering why we ask.
    name: "Cum te strigăm când comanda e gata?", namePh: "Un nume sau o poreclă",
    tip: "Vrei să lași un bacșiș?", tipNone: "Fără",
    // The ASK and the LINE ITEM are different sentences. The summary below the
    // payment buttons reads „Produse / Bacșiș / Total", and printing the
    // question there put „Vrei să lași un bacșiș?" in a column of labels beside
    // a sum the guest has already agreed to — a question answered several taps
    // ago, asked again in the place where the bill is totted up. The order page
    // has always called this row „Bacșiș" (src/guest/OrderStatus.tsx); this is
    // the same row and now says the same word.
    tipLine: "Bacșiș",
    tipOther: "Alt %", tipOtherPh: "%",
    receipt: "Trimite-mi bonul pe email (opțional)", emailPh: "example@gmail.com",
    /*
     * DATELE PLĂTITORULUI — și exact trei, fiindcă maib acceptă exact trei.
     *
     * `payerInfo` primește nume, email și telefon. NU are câmp de adresă, de
     * oraș sau de cod poștal — așa că nu le cerem. Date pe care nu le putem
     * trimite nicăieri sunt date pe care nu avem de ce să le strângem.
     *
     * `payerHint` spune deschis unde ajung. Un câmp de email lângă un buton de
     * plată, fără o vorbă despre bancă, e o presupunere pusă în cârca omului.
     */
    payer: "Datele plătitorului",
    payerHint: "Le trimitem băncii, odată cu plata.",
    // A SHAPE, not a word. „email" and „telefon" told the guest what the box
    // is; an example tells them what a finished answer looks like, prefix and
    // spacing included. Same example in all three languages — an address and a
    // number are not Romanian or Russian.
    payerEmailPh: "example@gmail.com", payerPhonePh: "+373 777 888 999", payerNamePh: "nume și prenume",
    payerEmailBad: "Verifică adresa de email.",
    /*
     * DOUĂ ȘIRURI PENTRU O PROPOZIȚIE, fiindcă mijlocul ei e un link.
     *
     * „Am citit și accept" e textul care ține de bifă; „termenii și condițiile"
     * e ancora. Despărțite ca să nu punem markup într-un dicționar și ca
     * traducătorul să vadă exact ce se subliniază — în rusă și engleză ordinea
     * cuvintelor e alta, dar bucata subliniată e aceeași noțiune.
     */
    terms: "Am citit și accept", termsLink: "termenii și condițiile",
    termsRequired: "Bifează că accepți termenii și condițiile ca să poți trimite comanda.",
    // MOD BAR: sesiunea personalului a expirat sau contul nu poate încasa.
    barDenied: "Nu poți lua comenzi la bar cu acest cont. Autentifică-te din nou la /staff.",
    barPlace: "Trimite comanda · plata la bar",
    /*
     * NU „mai încearcă", CI „mai așteaptă o clipă".
     *
     * Se vede doar în fereastra dintre deschiderea coșului și sosirea meniului
     * casei — o reîncărcare pe #checkout, o filă readusă din fundal. Butonul e
     * deja mort acolo; asta e propoziția care spune de ce, fiindcă un buton
     * mort care nu explică nimic e un capăt de drum.
     */
    notReady: "O clipă — încă încărcăm regulile casei.",
    /*
     * SCOATEREA ÎNTREABĂ ÎNTÂI. Minusul de la 1 în jos și ×-ul fac același
     * lucru — linia dispare cu totul — și amândouă o spun înainte s-o facă,
     * în locul rândului, nu peste el: un dialog modal pentru un rând de coș
     * e artilerie pentru o muscă.
     */
    remove: "Scoate", removeAsk: "Scoatem din comandă?",
    removeYes: "Da, scoate", removeNo: "Rămâne",
    method: "Cum plătești", mia: "MIA", miaHint: "din aplicația băncii",
    // Named in full — Apple Pay and Google Pay ride the same card rail on
    // maib's hosted page, and a guest holding a phone with no plastic at the
    // table reads plain „Card" as "not for me".
    card: "Card, Apple Pay, Google Pay", cash: "La bar, în numerar",
    subtotal: "Produse", total: "Total",
    place: "Comandă", payNow: "Plătește acum", placing: "Se trimite…",
    back: "Înapoi la meniu", soldOut: "Ne pare rău, s-a terminat:",
    error: "Nu am putut trimite comanda. Mai încearcă o dată.",
    over: "Evenimentul s-a încheiat. Nu se mai pot trimite comenzi.",
    unavailable: "Ceva din coș nu se mai servește acum. Scoate-l și încearcă din nou.",
    /*
     * THE RAIL IS DOWN, AND THAT IS NOT "mai încearcă o dată".
     *
     * `payment_unavailable` means the bank refused to open a payment page at
     * all. Tapping again cannot mend that.
     *
     * IT DOES NOT SAY "PICK CASH INSTEAD", AND THE FIRST DRAFT DID.
     *
     * That sentence was checked against the code and is false. The order row
     * already exists (worker/api/orders.ts keeps it, and its stock hold, on
     * purpose) and `idempotency_key` is minted once per CART — src/guest/cart.ts
     * rotates it only on a successful order — so tapping again with cash
     * selected hits the dedup fast path and returns the SAME card order,
     * untouched. Cash never reaches the server. And that order cannot be
     * settled by anyone: `POST /admin/bar/mark-paid` accepts only
     * `unpaid_cash_pending`, `POST /orders/:id/pay` accepts only `failed`, and
     * this one is `awaiting_payment` until its hold expires.
     *
     * So it points at the only thing that actually works — a person at the
     * counter — and names the code so they can be told which order it is.
     */
    payDown: "Plata cu cardul nu merge acum. Spune la bar codul comenzii tale:",
    /*
     * ACEEAȘI VESTE, DAR DESPRE METODA PE CARE A ALES-O OMUL.
     *
     * MIA și cardul merg azi pe același drum — pagina găzduită de maib, unde
     * MIA e o „plăcuță" (vezi getProvider în worker/payments/index.ts) — dar
     * omul a apăsat MIA, și un mesaj despre card l-ar face să creadă că a
     * greșit ceva. N-a greșit nimic. Ieșirea e aceeași: codul, spus la bar.
     */
    payDownMia: "Plata prin MIA nu merge acum. Spune la bar codul comenzii tale:",
    nameRequired: "Scrie un nume, ca să știm pe cine strigăm.",
    emailRequired: "Scrie adresa de email sau debifează.",
    /*
     * EMAILUL E OBLIGATORIU LA ORICE PLATĂ ONLINE — cerința băncii, nu a
     * noastră. Certificarea maib cere o confirmare pe email pentru fiecare
     * client, așa că adresa nu mai e o bifă opțională când banii trec printr-o
     * bancă. La numerar rămâne cum a fost: opțională.
     */
    emailConfirmHint: "Aici trimitem confirmarea plății.",
    emailRequiredOnline: "Scrie adresa de email — acolo trimitem confirmarea plății.",
    emailRequiredCash: "Scrie adresa de email — acolo îți trimitem bonul.",
    phoneRequired: "Scrie numărul de telefon ca să putem trimite comanda.",
    contactTitle: "Date de contact",
    cashEmailHint: "Aici îți trimitem bonul.",
    // Dismisses the refusal pop-up. „Am înțeles", the same words the ready
    // pop-up uses — one vocabulary for "I have read this".
    errOk: "Am înțeles",
  },
  ru: {
    title: "Ваш заказ", empty: "Корзина пуста.",
    name: "Как вас позвать, когда заказ будет готов?", namePh: "Имя или прозвище",
    tip: "Оставите чаевые?", tipNone: "Без",
    tipLine: "Чаевые",
    tipOther: "Другой %", tipOtherPh: "%",
    receipt: "Прислать чек на email (необязательно)", emailPh: "example@gmail.com",
    payer: "Данные плательщика",
    payerHint: "Передаём их банку вместе с оплатой.",
    payerEmailPh: "example@gmail.com", payerPhonePh: "+373 777 888 999", payerNamePh: "имя и фамилия",
    payerEmailBad: "Проверьте адрес email.",
    terms: "Я прочитал(а) и принимаю", termsLink: "условия и положения",
    termsRequired: "Отметьте, что принимаете условия и положения, чтобы отправить заказ.",
    barDenied: "С этой учётной записью нельзя принимать заказы у бара. Войдите заново на /staff.",
    barPlace: "Отправить заказ · оплата у бара",
    notReady: "Секунду — ещё загружаем правила заведения.",
    remove: "Убрать", removeAsk: "Убрать из заказа?",
    removeYes: "Да, убрать", removeNo: "Оставить",
    method: "Оплата", mia: "MIA", miaHint: "из приложения банка",
    card: "Карта, Apple Pay, Google Pay", cash: "Наличными в баре",
    subtotal: "Товары", total: "Итого",
    place: "Заказать", payNow: "Оплатить", placing: "Отправляем…",
    back: "Назад в меню", soldOut: "Извините, закончилось:",
    error: "Не удалось отправить заказ. Попробуйте ещё раз.",
    over: "Мероприятие закончилось. Заказы больше не принимаются.",
    unavailable: "Что-то из корзины сейчас не подаётся. Уберите это и попробуйте снова.",
    payDown: "Оплата картой сейчас не работает. Назовите в баре код вашего заказа:",
    payDownMia: "Оплата через MIA сейчас не работает. Назовите в баре код вашего заказа:",
    nameRequired: "Напишите имя, чтобы мы знали, кого звать.",
    emailRequired: "Укажите email или снимите галочку.",
    emailConfirmHint: "Сюда придёт подтверждение оплаты.",
    emailRequiredOnline: "Укажите email — туда придёт подтверждение оплаты.",
    emailRequiredCash: "Укажите email — туда придёт чек.",
    phoneRequired: "Укажите номер телефона, чтобы отправить заказ.",
    contactTitle: "Контактные данные",
    cashEmailHint: "Сюда придёт чек.",
    errOk: "Понятно",
  },
  en: {
    title: "Your order", empty: "Your basket is empty.",
    name: "What shall we call you when your order is ready?", namePh: "A name or a nickname",
    tip: "Leave a tip?", tipNone: "None",
    tipLine: "Tip",
    tipOther: "Other %", tipOtherPh: "%",
    receipt: "Email me the receipt (optional)", emailPh: "example@gmail.com",
    payer: "Payer details",
    payerHint: "We send these to the bank with the payment.",
    payerEmailPh: "example@gmail.com", payerPhonePh: "+373 777 888 999", payerNamePh: "full name",
    payerEmailBad: "Please check the email address.",
    terms: "I have read and accept the", termsLink: "terms and conditions",
    termsRequired: "Tick the box to accept the terms and conditions before ordering.",
    barDenied: "This account cannot take orders at the bar. Sign in again at /staff.",
    barPlace: "Place order · paid at the bar",
    notReady: "One moment — we're still loading the house's rules.",
    remove: "Remove", removeAsk: "Remove from the order?",
    removeYes: "Yes, remove", removeNo: "Keep",
    method: "How you pay", mia: "MIA", miaHint: "from your banking app",
    card: "Card, Apple Pay, Google Pay", cash: "Cash at the bar",
    subtotal: "Items", total: "Total",
    place: "Order", payNow: "Pay now", placing: "Sending…",
    back: "Back to the menu", soldOut: "Sorry, this ran out:",
    error: "We couldn't send the order. Please try once more.",
    over: "The evening has ended. Orders are closed.",
    unavailable: "Something in your basket is no longer being served. Remove it and try again.",
    payDown: "Card payment isn't working right now. Give the bar your order code:",
    payDownMia: "MIA isn't working right now. Give the bar your order code:",
    nameRequired: "Write a name, so we know who to call.",
    emailRequired: "Write your email, or untick the box.",
    emailConfirmHint: "The payment confirmation goes here.",
    emailRequiredOnline: "Write your email — the payment confirmation goes there.",
    emailRequiredCash: "Write your email — your receipt goes there.",
    phoneRequired: "Enter a phone number to place the order.",
    contactTitle: "Contact details",
    cashEmailHint: "Your receipt goes here.",
    errOk: "Got it",
  },
} as const;

/**
 * A TIP IS A PROPORTION, NOT A NUMBER.
 *
 * It was four fixed sums — 0, 10, 20 and 50 lei — which asks the guest to do
 * the arithmetic the interface should be doing, and asks it differently
 * depending on the basket: 20 lei is generous on a coffee and thin on a table
 * of six. The percentages carry the same intent at every size.
 *
 * `Math.round` on bani, so the total is always an exact integer of minor units.
 * A percentage that produced a fraction of a ban would be a rounding argument
 * with the bank at settlement.
 */
const FALLBACK_TIP_PERCENTS = [10, 20, 25];

const tipFor = (subtotalMinor: number, percent: number): number =>
  Math.round((subtotalMinor * percent) / 100);

type Method = "mia" | "card" | "cash";

/**
 * WHICH METHOD IS ALREADY CHOSEN WHEN THE SCREEN OPENS: CARD, when the house
 * takes it, then MIA, then whatever is first.
 *
 * The third answer this function has given, each on its own evidence. First it
 * was `methods[0]` — an order nobody chose, just what the settings screen
 * happened to store. Then MIA, on the argument that instant transfers settle
 * by morning and reconcile themselves. Owner's call, 2026-08-11, the day maib
 * enabled MIA on the hosted page: CARD first. Every guest has a card and every
 * card behaves the same; MIA needs a Moldovan banking app the tourist at the
 * next table does not have, so it stays one visible tap away rather than being
 * the thing a foreigner has to notice and un-pick.
 *
 * The fallback is the first method offered, so a cash-only house is unchanged.
 */
function preferredMethod(offered: Method[]): Method {
  if (offered.includes("card")) return "card";
  if (offered.includes("mia")) return "mia";
  return offered[0] ?? "cash";
}

/**
 * The payment step, drawn rather than imported.
 *
 * A banknote: square-cornered like everything else here, and deliberately not
 * a card, because the guest may be about to pay by bank app or in coins at the
 * bar. It says "money changes hands now", which is the one thing the button
 * below it was not saying — "Comandă" alone reads like adding another item.
 *
 * Hand-drawn for the same reason worker/api/images.ts draws its own: the brief
 * asks for almost no iconography and no icon-library personality. `currentColor`
 * so it inherits the button's paper-on-clay and its disabled state.
 */
function PaymentMark() {
  return (
    <svg
      width="22" height="22" viewBox="0 0 24 24" fill="none"
      aria-hidden="true" focusable="false" className="shrink-0"
    >
      {/* No `strokeLinejoin="round"` on the frame: the corners stay mitred, so
          the note is square like everything else. Stroke 2 and not 1.7 —
          rendered at 22/24 that is 1.8px of white on clay, and a hairline in
          the direct sunlight this screen is read in is no mark at all. */}
      <rect x="2" y="6" width="20" height="12" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="2.9" stroke="currentColor" strokeWidth="2" />
      <path d="M5.4 9.8v4.4M18.6 9.8v4.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The same chevron the menu's bar carries, for the same reason: this bar leaves
 * the app for the bank's page, and a mark at the trailing edge is how a phone
 * says so. See the long note on `GoMark` in src/guest/Menu.tsx for why a
 * chevron is the one glyph the briefs leave standing.
 *
 * Not drawn for CASH. Nothing is about to open — the guest walks to the bar —
 * and an arrow promising a next screen that never comes is worse than no arrow.
 */
function GoMark() {
  return (
    <svg
      width="20" height="20" viewBox="0 0 24 24" fill="none"
      aria-hidden="true" focusable="false" className="shrink-0"
    >
      <path
        d="M9 5l7 7-7 7"
        stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The remove mark on a basket line: a bin, hand-drawn in the same stroke
 * vocabulary as every other glyph here (see PaymentMark) — never imported
 * from an icon set. It began life as an ×, and the owner asked for something
 * that SAYS delete: an × beside a stepper reads as "close" or "wrong", a bin
 * reads as nothing else. Square-shouldered, like everything on this paper.
 */
function RemoveMark() {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none"
      aria-hidden="true" focusable="false" className="shrink-0"
    >
      {/* Lid, handle, body, two ribs — five strokes, mitred corners. */}
      <path d="M4 7h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M9.5 7V4.5h5V7" stroke="currentColor" strokeWidth="2" />
      <path d="M6.5 7l1 13.5h9l1-13.5" stroke="currentColor" strokeWidth="2" />
      <path d="M10 10.5v6.5M14 10.5v6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * WHAT THIS GUEST TIPPED LAST TIME, ON THIS PHONE.
 *
 * A PERCENTAGE, never a sum of money, and the distinction is the whole design:
 * remembering "45 lei" and re-applying it to a second round of two coffees
 * would propose a tip larger than the order. A percentage is the thing the
 * guest actually chose — the buttons are percentages — and it scales with
 * whatever they order next, which is what "the same tip" means to a person.
 *
 * Written only when an order is genuinely PLACED, so a guest who taps 15% and
 * then abandons the basket has not taught the phone anything. Zero is recorded
 * as deliberately as 10 is: declining is a choice, and a guest who has just
 * said no should not be asked again with a pre-filled yes.
 *
 * NOT A HOUSE DEFAULT. It is the guest's own last explicit choice, on their own
 * phone, still one tap from „Fără" — which is the line between remembering a
 * preference and the pre-ticked tip box every honest bar is judged for. It is
 * never applied where the house has switched tipping off, and never on a phone
 * that has not placed an order here before.
 */
const TIP_MEMORY_KEY = "prispa.tip";

function rememberedTip(): number | null {
  const raw = localStorage.getItem(TIP_MEMORY_KEY);
  if (raw === null) return null;
  const n = Number(raw);
  // Storage is guest-writable and survives deploys; a value that is no longer
  // a percentage is discarded rather than trusted.
  return Number.isInteger(n) && n >= 0 && n <= 100 ? n : null;
}

function rememberTip(percent: number): void {
  try {
    localStorage.setItem(TIP_MEMORY_KEY, String(percent));
  } catch {
    /* quota — the next order simply starts from nothing, as it always did */
  }
}

/**
 * „TRIMITE-MI BONUL PE EMAIL", REMEMBERED — AND THE ADDRESS WITH IT.
 *
 * ONE KEY HOLDS BOTH FACTS, and that is the design rather than a shortcut:
 * its PRESENCE is the guest asking for receipts, and its VALUE is where to
 * send them. There is deliberately no way to store an address without the
 * request, because that is precisely the pairing `orders` refuses at the
 * database level —
 *
 *     CHECK (contact_consent = 1 OR (guest_phone IS NULL AND guest_email IS NULL))
 *
 * — in migration 0004. An address kept on the phone of a guest who has since
 * said "no receipt" would be contact data retained after the reason for
 * holding it ended, which is the same mistake in a smaller box; so placing an
 * order with the box unticked FORGETS the address rather than merely
 * remembering a "false" beside it.
 *
 * It never leaves the device on its own. It is typed by the guest, kept in
 * their own browser, and travels only on an order they place themselves —
 * exactly as it did when they retyped it every time.
 */
const RECEIPT_MEMORY_KEY = "prispa.receipt";

/**
 * THE PAYER'S PHONE, remembered on the same terms as the receipt address.
 *
 * A separate key, because it answers a different question and is given for a
 * different reason: the address is "send me a receipt", this is "here is who is
 * paying", and the bank is what wants it. Collapsing them into one key would
 * mean a guest who stops asking for receipts silently keeps a phone number on
 * file, which is the retention mistake the note above exists to avoid.
 *
 * So it follows the same rule in its own right: it is stored only while there
 * is a number to store, and placing an order with the field emptied FORGETS it.
 */
const PHONE_MEMORY_KEY = "prispa.payerphone";

function rememberedPhone(): string | null {
  const raw = localStorage.getItem(PHONE_MEMORY_KEY);
  // Digits and the punctuation people type between them, nothing else — the
  // same shape this screen would have accepted, not trusted blindly back out.
  return raw && /^[+\d][\d\s()\-.]{5,24}$/.test(raw) ? raw : null;
}

/**
 * Digits and one leading „+", nothing else. A number arrives pasted from a
 * contacts app or typed with the spaces people speak in — „+373 777 888 999",
 * dots, dashes, even a stray newline riding along with a paste — and the
 * bank's `payerInfo` wants a phone, not typography. Run at SEND and at
 * REMEMBER, never against the box while somebody is still typing in it.
 */
function normalizePhone(value: string): string {
  const v = value.trim();
  return (v.startsWith("+") ? "+" : "") + v.replace(/\D/g, "");
}

/**
 * THE NAME AND THE EMAIL, remembered on the same terms as the phone.
 *
 * Owner's call, 2026-08-12: once a guest has filled their details in, the
 * SECOND order opens already knowing them — name, tip, email, phone —
 * because retyping at a table is how a checkout gets abandoned. Everything
 * here lives in the guest's own browser and travels only on an order they
 * place themselves, exactly as the tip and the receipt address always have.
 *
 * WRITTEN ONLY ON A PLACED ORDER, and only when the field actually said
 * something: an empty name on a cash order where the house asked for none
 * must not erase what the guest taught us last time. The phone keeps its own
 * stricter rule — see `rememberHabits` — because it is the one field with a
 * visible way to be deliberately emptied.
 */
const NAME_MEMORY_KEY = "prispa.payername";
const EMAIL_MEMORY_KEY = "prispa.payeremail";

function rememberedName(): string | null {
  const raw = localStorage.getItem(NAME_MEMORY_KEY);
  return raw?.trim() ? raw.trim() : null;
}

function rememberName(value: string): void {
  try {
    if (value.trim()) localStorage.setItem(NAME_MEMORY_KEY, value.trim());
  } catch {
    /* quota — it is only a convenience */
  }
}

function rememberedEmail(): string | null {
  const raw = localStorage.getItem(EMAIL_MEMORY_KEY);
  // The same shape the form itself requires before it will send one.
  return raw && raw.includes("@") ? raw : null;
}

function rememberEmail(value: string): void {
  try {
    if (value.includes("@")) localStorage.setItem(EMAIL_MEMORY_KEY, value);
  } catch {
    /* quota — it is only a convenience */
  }
}

function rememberPhone(value: string): void {
  try {
    const v = value.trim();
    if (v) localStorage.setItem(PHONE_MEMORY_KEY, v);
    else localStorage.removeItem(PHONE_MEMORY_KEY);
  } catch {
    /* quota — it is only a convenience */
  }
}

function rememberedReceipt(): string | null {
  const raw = localStorage.getItem(RECEIPT_MEMORY_KEY);
  // The same shape the form itself requires; anything else is not an address
  // this screen would have accepted, so it is not trusted back out of storage.
  return raw && raw.includes("@") ? raw : null;
}

function rememberReceipt(wanted: boolean, address: string): void {
  try {
    if (wanted && address.includes("@")) localStorage.setItem(RECEIPT_MEMORY_KEY, address);
    else localStorage.removeItem(RECEIPT_MEMORY_KEY);
  } catch {
    /* quota — the field simply starts empty next time, as it always did */
  }
}

/**
 * THE CHECKOUT IN PROGRESS, SO A REFRESH IS NOT A RESTART.
 *
 * The basket has survived a reload since the first version (src/guest/cart.ts,
 * "in Pohrebea that is not an edge case") — and then everything the guest typed
 * ON TOP of it did not. Name, tip, receipt address, payment method were all
 * component state in a sheet that only exists while `cartOpen` is true, so a
 * refresh, an accidental Back, or iOS discarding a backgrounded tab returned
 * them to the menu with a full basket and an empty form, to fill in again on
 * one bar of signal.
 *
 * This is deliberately SEPARATE from `prispa.tip` and `prispa.receipt` above,
 * which are habits carried between orders. This is one unfinished order,
 * scoped to its event, and it is deleted the moment that order is placed.
 */
type Draft = {
  name: string;
  tipPercent: number;
  customPercent: string;
  customOpen: boolean;
  wantReceipt: boolean;
  email: string;
  /**
   * The payer's phone, for `payerInfo`. In the draft rather than in a habit
   * key like the tip and the receipt address, because it is typed once per
   * unfinished order and must survive a reload — but see `rememberedPhone`:
   * a number IS carried between orders, exactly as the receipt address is.
   */
  phone: string;
  method: Method | null;
  /*
   * WITHIN ONE UNFINISHED ORDER, AND NEVER BEYOND IT.
   *
   * The tip and the receipt address are habits, kept in `prispa.tip` and
   * `prispa.receipt` and carried between orders. Acceptance of the terms is
   * deliberately NOT one of those: there is no „prispa.terms", so a guest who
   * accepted last Saturday finds the box empty tonight. Agreeing once is not a
   * standing agreement, and a box that arrives ticked is the formality this
   * feature exists not to be.
   *
   * It rides the draft only so that a refresh in the middle of one checkout —
   * an accidental Back, iOS discarding a backgrounded tab — is not a restart,
   * which is the whole point of the draft. Same order, same tick.
   */
  termsOk: boolean;
};

const draftKey = (slug: string) => `prispa.draft.${slug}`;

function readDraft(slug: string): Partial<Draft> | null {
  try {
    const raw = localStorage.getItem(draftKey(slug));
    return raw ? (JSON.parse(raw) as Partial<Draft>) : null;
  } catch {
    // A corrupt draft is not worth blocking an order over; the form simply
    // starts from the remembered habits instead.
    return null;
  }
}

function writeDraft(slug: string, d: Draft): void {
  try {
    localStorage.setItem(draftKey(slug), JSON.stringify(d));
  } catch {
    /* quota — the form still works, it just will not survive a reload */
  }
}

function clearDraft(slug: string): void {
  localStorage.removeItem(draftKey(slug));
}

/**
 * THE FACE THE TIP MAKES — owner's ask, 2026-08-16.
 *
 * One emoji under the picker, following the CURRENT choice: quietly sad at
 * „Fără", warmer with every step up, delighted past 20%. The nudge is the
 * whole point and it is an honest one — a face is a sentence about how the
 * choice lands, said without a word of copy in any language, which is also
 * why it needs no entry in the three translation tables.
 *
 * It follows `effectivePercent`, so a custom „Alt %" earns its face too, and
 * a custom 35% earns the best one. DECORATIVE ONLY: aria-hidden at the call
 * site, and never part of any payload — the tip that travels is a number,
 * and the bank's own line stays the word „Bacșiș" (worker/api/orders.ts).
 */
function tipFace(percent: number): string {
  if (percent <= 0) return "🙁";
  if (percent < 10) return "🙂";
  if (percent < 15) return "😊";
  if (percent < 20) return "😄";
  if (percent <= 20) return "🤩";
  // Past the last preset there is nothing left to upsell — only to thank.
  // Owner's ask, 2026-08-16: 21% and above gets the kiss.
  return "😘";
}

/**
 * 0–1000, integers only. Anything else is not a tip the guest meant to leave.
 *
 * The ceiling was 100, which read as natural — "a percentage" — and was wrong
 * the first time somebody generous met it: a guest doubling a small round for
 * the band could not (owner's call, 2026-08-16: the cap is 1000). The AMOUNT
 * still has its own guard: the server refuses any tip over 10 000 lei
 * (worker/api/orders.ts, tip_minor max), and the checkout prints the computed
 * sum under the picker before anything is sent — the percentage is the input,
 * the sum is the fact, and the fact is what the guest confirms.
 */
function parsePercent(raw: string): number | null {
  const cleaned = raw.trim().replace(",", ".");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0 || n > 1000) return null;
  return Math.round(n);
}

/**
 * One screen, and only one thing is required: a name.
 *
 * Everything else has a working default — MIA is preselected because it is the
 * rail we want and the one most guests already have, the tip starts at nothing,
 * and the receipt field only appears if you ask for it. A guest who taps
 * "Comandă" straight away gets a valid order.
 */
export function Checkout(props: Props) {
  const t = T[(props.lang in T ? props.lang : "ro") as keyof typeof T];
  /*
   * THE NAME BOX STARTS EMPTY — the place off the QR card is not a name.
   *
   * It used to be pre-filled with the zone (`Masa ${zone}` at first, then the
   * house's own label for it), and the owner has now asked for the QR name to
   * be out of the box entirely: a guest who kept the default went into the
   * order book, the bar board and the receipt as „Pergola 3", and the order
   * page printed the same word twice — once as a name, once as the place.
   *
   * NOTHING IS LOST BY THIS. The zone still travels on the order as its own
   * field (`zone_code`, sent below), the staff board prints it beside the
   * name, and an order left nameless still falls back to the place at submit —
   * so the tray still finds its corner; it just stops pretending the corner
   * ordered the drinks.
   */
  /*
   * Everything below opens from THREE sources, in this order of authority:
   *   1. the unfinished draft for this event — what the guest was typing;
   *   2. what they chose on their last order here — the remembered habits;
   *   3. the plain defaults.
   * Read once, at mount, so nothing can yank a field out from under a thumb.
   */
  /*
   * AND NONE OF THE THREE APPLY AT THE COUNTER.
   *
   * All three are memories of ONE person — the guest whose phone this is —
   * and mod bar serves a queue of strangers on a device that is nobody's. A
   * draft restored there would open the next customer's order pre-filled with
   * the last one's name, and `rememberedEmail()` would put a real guest's
   * address on a screen a member of staff is looking at. So the counter starts
   * empty every time and leaves no trace when it is done (`saveDraft` below is
   * skipped for the same reason).
   */
  const bar = props.bar === true;
  const draft = useState(() => (bar ? null : readDraft(props.eventSlug)))[0];

  // A draft that only held the old pre-fill is the pre-fill wearing a
  // disguise; treat it as empty rather than resurrecting the QR name.
  const draftName =
    draft?.name && draft.name !== props.zoneLabel && draft.name !== props.zone ? draft.name : "";
  // The draft first (what they were just typing), then the name their last
  // placed order travelled under — see NAME_MEMORY_KEY.
  const [name, setName] = useState(() => (bar ? "" : draftName || rememberedName() || ""));
  // The chosen percentage, and the free-entry box that overrides it. `custom`
  // is a string because a half-typed "1" on the way to "15" must not snap the
  // total to 1%.
  // Zero is always first and is not a "percentage the house offers" — it is
  // the way out of the question.
  const offeredPercents = [0, ...(props.rules?.tip_percents ?? FALLBACK_TIP_PERCENTS)];
  const askForTip = props.rules?.tips_enabled ?? true;
  const methods = (props.rules?.payment_methods ?? ["cash"]) as Method[];
  const nameRequired = props.rules?.require_guest_name ?? true;
  /*
   * THE HOUSE'S TERMS, AND WHETHER THERE ARE ANY.
   *
   * `undefined` on a pre-v5 snapshot, "" for a house that has published none,
   * and both mean the same: no box, no gate, nothing changes for that house.
   * A checkbox linking nowhere would ask the guest to agree to a document
   * nobody can read, which is worse than not asking — so the ABSENCE of a
   * terms address is what decides, not a separate switch that could disagree
   * with it.
   *
   * `.trim()` because the setting is free text typed by a person; a value of
   * one space must not draw a link to one space.
   */
  const termsUrl = props.rules?.terms_url?.trim() ?? "";
  /*
   * „NO TERMS" AND „WE DO NOT YET KNOW" ARE NOT THE SAME ANSWER.
   *
   * `props.rules` is null until the menu snapshot lands, and `termsUrl` reads
   * "" through that whole window — which the three defences above and below all
   * take to mean „this house published none". So they ALL failed open together,
   * and the backstop in `place()` could not catch what the disabled button
   * missed, because both were reading the same empty string.
   *
   * That window is reachable in a plain browser with no devtools. App.tsx opens
   * this sheet synchronously from `location.hash === "#checkout"` with the
   * basket restored from localStorage, as a SIBLING of the menu — so a guest who
   * reloads on the checkout, or whose backgrounded iOS tab is restored, sees a
   * fully interactive `fixed inset-0` sheet on top of the menu's loading screen
   * while `rules` is still null. Tapping the bar there posted a cash order with
   * no acceptance box ever having been drawn. On the connections this app is
   * built for, that is seconds, not milliseconds.
   *
   * The sibling rule one line up is the model: `require_guest_name` defaults to
   * `true` on unknown, i.e. it fails CLOSED. So does this. And it is right
   * beyond the terms: a checkout that does not yet know the house's rules cannot
   * honestly draw its tip presets or its payment methods either, so refusing to
   * submit until they land is the only accurate thing it can do.
   */
  const rulesKnown = props.rules !== null;

  /*
   * The tip opens on the draft, or failing that on whatever this guest chose
   * last time (`rememberedTip`). A phone that has neither keeps the original
   * behaviour exactly: nothing selected but „Fără".
   *
   * WHICH BUTTON THAT LANDS ON is decided later, not here. `props.rules` may
   * still be null at mount — the snapshot is what carries `tip_percents` — so
   * a percentage cannot be matched against the house's buttons yet, and
   * guessing from the fallback list would light no chip at all for a house
   * that offers 5/10/15 to a guest who last left 20. The reconciliation effect
   * below does it once the rules land.
   */
  const [tipPercent, setTipPercent] = useState(draft?.tipPercent ?? rememberedTip() ?? 0);
  const [customPercent, setCustomPercent] = useState(draft?.customPercent ?? "");
  const [customOpen, setCustomOpen] = useState(draft?.customOpen ?? false);
  // Ticked, with the address filled in, for a guest who asked for a receipt
  // last time — see `rememberedReceipt`. One key, so the tick and the address
  // can never disagree. The draft wins where there is one: a guest who had
  // just UNticked it and refreshed must not find it ticked again.
  const [wantReceipt, setWantReceipt] = useState(
    () => (bar ? false : draft?.wantReceipt ?? rememberedReceipt() !== null)
  );
  // Draft, then the payer habit, then the receipt habit — two keys because
  // they answer different questions (see EMAIL_MEMORY_KEY and
  // RECEIPT_MEMORY_KEY), but on this screen they fill the same one box.
  const [email, setEmail] = useState(
    () => (bar ? "" : draft?.email ?? rememberedEmail() ?? rememberedReceipt() ?? "")
  );
  /*
   * ONE EMAIL BOX ON THE SCREEN, TWO PURPOSES, AND THE SAME PERSON.
   *
   * The address the bank is given and the address a receipt goes to are the
   * guest's own, and asking twice on a phone at a table is how a checkout gets
   * abandoned. So `email` above serves both: the payer block renders it for a
   * card payment, the receipt tick reuses it, and neither draws a second box.
   *
   * The phone is new and has nowhere else to live.
   */
  const [phone, setPhone] = useState(() => (bar ? "" : draft?.phone ?? rememberedPhone() ?? ""));
  /*
   * UNTICKED UNLESS THIS VERY CHECKOUT WAS ALREADY TICKED.
   *
   * `draft?.termsOk ?? false` — never `?? true`, never remembered from a
   * previous order, and never derived from anything the house configured. The
   * only thing that can start this true is the guest having ticked it minutes
   * ago on this same unfinished order, before a reload.
   */
  const [termsOk, setTermsOk] = useState(() => draft?.termsOk ?? false);
  /*
   * The guest's choice, or — until they make one — card. See `preferredMethod`.
   *
   * DERIVED, NOT INITIALISED. `useState(preferredMethod(methods))` would read
   * `methods` exactly once, on mount, and `props.rules` is null until the menu
   * snapshot lands — so the preselected method would be frozen to the "cash"
   * fallback of a screen that had not yet been told what the house takes. It
   * also keeps the selection HONEST afterwards: if the house stops offering the
   * method already selected, the screen cannot sit on a button the server will
   * refuse.
   */
  const [picked, setPicked] = useState<Method | null>(draft?.method ?? null);
  /*
   * AT THE COUNTER THERE IS NOTHING TO CHOOSE. „Plata la bar" is the entire
   * reason this mode exists — there is no card terminal in it and no guest
   * holding a phone to be redirected to a bank — so the method is a constant
   * and the picker below is not drawn. The server agrees in the other
   * direction: a bar order naming anything but cash is refused outright rather
   * than quietly corrected (`bar_order_cash_only`).
   */
  const method: Method = bar ? "cash" : picked && methods.includes(picked) ? picked : preferredMethod(methods);
  /*
   * ANY PAYMENT WITH A BANK IN IT — card AND MIA, one block for both.
   *
   * The second answer this constant has given. It was card-only, on the
   * argument that MIA authenticates the payer inside their own banking app and
   * a name we would only forward to the same bank is friction. Owner's call,
   * 2026-08-12: the two online rails collect the SAME payer details, in the
   * same block — maib's certification wants the confirmation email either way,
   * and two rails that ask different questions read as two different levels of
   * trust when they are the same hosted page underneath.
   *
   * Cash still skips the whole block. Nobody's bank is involved, and a counter
   * does not need an email address.
   */
  const needsPayer = method !== "cash";
  /*
   * WHAT THIS HOUSE DEMANDS FOR THIS METHOD — client's ask, 2026-08-14,
   * carried by snapshot v8. Absent (pre-v8 snapshot, rules not landed) reads
   * as the defaults: online wants the email the bank requires, cash wants
   * nothing. `phone: true` makes the number a gate on any rail; `email: true`
   * on cash forces the address (and with it the receipt — that is what the
   * address is FOR on a cash order, and the hint under the box says so).
   */
  const contactWanted = bar
    // Nothing is asked of a guest who is standing at the counter rather than
    // typing — same exemption the server applies, and for the same reason:
    // staff cannot give another person's consent. See `wanted` in
    // worker/api/orders.ts.
    ? { email: false, phone: false }
    : props.rules?.checkout_contact?.[method] ?? { email: method !== "cash", phone: false };
  const phoneRequired = contactWanted.phone === true;
  /*
   * The confirmation address rides the payer block now, but the REQUIREMENT
   * keeps its own name: maib's certification requires a confirmation email to
   * every online customer (`email_required` on the server), while the name and
   * the phone stay optional. Cash keeps the opt-in receipt tick instead.
   */
  const needsEmail = needsPayer || contactWanted.email === true;
  const setMethod = setPicked;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * WHICH FIELD THE REFUSAL IS ABOUT, so the screen can walk the guest there.
   *
   * The pop-up used to name the missing name and leave the guest to find the
   * box — half a screen up, behind the keyboard, on a checkout they had
   * already scrolled past. Now the refusal scrolls its field to the middle of
   * the screen, wears the danger colour on it until the guest answers, and
   * „Am înțeles" puts the cursor in it. Cleared the moment the field is
   * touched, together with the sentence.
   */
  const [errorField, setErrorField] = useState<"name" | "email" | "phone" | "terms" | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const termsRef = useRef<HTMLInputElement | null>(null);

  const fieldRef = (field: typeof errorField) =>
    field === "name" ? nameRef
      : field === "email" ? emailRef
      : field === "phone" ? phoneRef
      : field === "terms" ? termsRef
      : null;

  /** A validation refusal: the sentence, the field it is about, the scroll. */
  function refuse(msg: string, field: typeof errorField) {
    setError(msg);
    setErrorField(field);
    fieldRef(field)?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  const clearRefusal = () => { setError(null); setErrorField(null); };

  /** The border a field wears while a refusal points at it. */
  const flagged = (field: typeof errorField) =>
    errorField === field ? { borderColor: "var(--color-danger)", borderWidth: 2 } : {};
  /*
   * THE LINE ABOUT TO LEAVE THE ORDER, or null. Reaching zero — by the minus
   * or by the × — does not remove; it asks, in the row itself. One id, not a
   * set: a second question opening closes the first, because two rows both
   * asking „scoatem?" is a quiz, not a checkout.
   */
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);

  const effectivePercent = customOpen ? (parsePercent(customPercent) ?? 0) : tipPercent;
  const tip = askForTip ? tipFor(props.subtotal, effectivePercent) : 0;
  const total = props.subtotal + tip;

  /*
   * WHICH TIP BUTTON A REMEMBERED PERCENTAGE BELONGS ON, once the house has
   * said which buttons it offers.
   *
   * `tip_percents` is per-tenant and arrives with the menu snapshot, so at
   * mount there is nothing to match against. A guest who last left 20% at a
   * house that offers 5/10/15 would otherwise sit on a screen with NO chip
   * lit and a 20% tip silently in the total — the worst of both, since the
   * only visible way to change it would be to notice the total was wrong.
   *
   * Runs ONCE, when the rules first land, and only while nothing has been
   * touched — `reconciled` latches immediately, so it can never reach in and
   * move a selection the guest has since made themselves.
   */
  const reconciled = useRef(false);
  useEffect(() => {
    if (reconciled.current || !props.rules) return;
    reconciled.current = true;
    if (customOpen || tipPercent <= 0) return;
    if (props.rules.tip_percents.includes(tipPercent)) return;
    // Not a button here — honour the number, in the box that can show it.
    setCustomOpen(true);
    setCustomPercent(String(tipPercent));
    setTipPercent(0);
  }, [props.rules, customOpen, tipPercent]);

  /*
   * The draft, written on every keystroke and every tap.
   *
   * Cheap — one small JSON string per change, to the same localStorage the
   * cart is already writing on every quantity change — and it is what makes
   * „refresh and carry on" true rather than nearly true.
   */
  useEffect(() => {
    // The counter writes none. See the note beside `draft` above: the draft is
    // one guest's unfinished order, and a shared device would hand it to the
    // next person in the queue.
    if (bar) return;
    writeDraft(props.eventSlug, {
      name, tipPercent, customPercent, customOpen, wantReceipt, email, phone, method: picked, termsOk,
    });
  }, [bar, props.eventSlug, name, tipPercent, customPercent, customOpen, wantReceipt, email, phone, picked, termsOk]);

  /*
   * EVERY HABIT THIS ORDER PROVED, kept for the next one. Called on both
   * paths that end with a real order row — the clean success and the
   * bank-refused `payment_unavailable` — because in both the guest's choices
   * were real. The name and the email update only when they actually said
   * something / travelled; the phone keeps the stricter rule (emptying the
   * drawn field forgets it, a cash order where it is not drawn leaves it be).
   */
  function rememberHabits() {
    // Nothing to remember, and nobody to remember it for: the habits belong to
    // a guest, and a counter device serves whoever is next in the queue.
    if (bar) return;
    rememberTip(effectivePercent);
    rememberReceipt(wantReceipt, email.trim());
    rememberName(name.trim());
    if (needsEmail || wantReceipt) rememberEmail(email.trim());
    if (needsPayer || phoneRequired) rememberPhone(normalizePhone(phone));
  }

  async function place() {
    // `nameRequired` is a rule about guests; the server waives it for a counter
    // order for the same reason (`require_guest_name && !staff`).
    if (nameRequired && !bar && !name.trim()) return refuse(t.nameRequired, "name");
    // Spaces, dashes and stray newlines off the number BEFORE it travels —
    // the bank wants a phone, not typography. The box keeps what was typed.
    const cleanPhone = normalizePhone(phone);
    /*
     * ONLINE FIRST, because it is the stricter rule: any payment with a bank
     * in it needs a usable address for the confirmation email — see
     * `needsEmail`. Cash falls back to the old contract: an address only if
     * the receipt box is ticked. Either way a typed address must be usable —
     * maib's own SDK runs it through FILTER_VALIDATE_EMAIL, and a malformed
     * value is the kind of thing that fails at the bank rather than here,
     * where nobody can explain it to the guest.
     */
    if (needsEmail && !email.includes("@")) {
      // Two different truths behind one gate: online, the bank's confirmation
      // goes there; on cash it is the bon the house has chosen to always send.
      return refuse(needsPayer ? t.emailRequiredOnline : t.emailRequiredCash, "email");
    }
    if (!needsEmail && wantReceipt && !email.includes("@")) return refuse(t.emailRequired, "email");
    if (phoneRequired && !cleanPhone) return refuse(t.phoneRequired, "phone");
    /*
     * THE ANSWER BEHIND THE GREY BUTTON — no longer a backstop nobody reaches.
     *
     * Since 2026-08-16 the bottom bar stays TAPPABLE while the terms are
     * unticked (grey, but alive — see the long note on the bar), so this line
     * is now the main road: the tap lands here and leaves as the house's full
     * refusal — the sentence, the scroll to the box, the danger colour on it.
     * It also still covers what it always covered: an Enter key on a form, a
     * future caller, a refactor that moves the bar. The one thing that must
     * not happen quietly is an order placed by somebody who never agreed to
     * the terms of the sale.
     *
     * `!rulesKnown` FIRST, and it is a separate sentence rather than a wider
     * condition on the same one: until the snapshot lands we do not know
     * whether this house has terms at all, and telling a guest at a house with
     * none to „tick the box" would point at a box that will never be drawn.
     */
    if (!rulesKnown) return setError(t.notReady);
    // Online only — cash hands the guest their contract at the counter, so no
    // box is drawn for it and none is demanded (owner's call, 2026-08-12; the
    // server's gate makes the same exception).
    if (termsUrl && needsPayer && !termsOk) return refuse(t.termsRequired, "terms");

    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/orders", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          event: props.eventSlug,
          zone_code: props.zone,
          // When the house does not ask, the board still needs SOMETHING to
          // shout. The place is what the runner walks to; the pickup code is
          // already on the card in the guest's hand.
          //
          // This fallback OUTLIVES the field's pre-fill, which is gone by the
          // owner's request: the QR name no longer sits in the box posing as
          // the guest, but an order left nameless where `require_guest_name`
          // is off still lands on the board with its place word — both screens
          // that print `guest_name` beside the zone dedup the echo.
          guest_name: name.trim() || props.zoneLabel || props.zone || "",
          // Online: always the confirmation address — validated three lines
          // up, required by the server. Cash: the guest's own tick, as ever.
          receipt_email: needsEmail || wantReceipt ? email.trim() : null,
          receipt_requested: needsEmail || wantReceipt,
          /*
           * THE PAYER, FOR THE BANK — and the consent that lets us hold it.
           *
           * `contact_consent` is what migration 0004 requires before an email or
           * a phone may be stored at all:
           *
           *     CHECK (contact_consent = 1 OR (guest_phone IS NULL AND guest_email IS NULL))
           *
           * It is set from whether the guest actually typed something into the
           * payer block, and the block says in plain words where those details
           * go. Filling it IS the consent; leaving it empty sends nothing and
           * stores nothing, which is why there is no separate tick to forget.
           *
           * Only for a payment with a bank in it. A cash order sends neither,
           * so a guest who types an address for a receipt does not thereby hand
           * a phone number to an acquirer.
           */
          guest_email: needsPayer && email.trim() ? email.trim() : null,
          // The number travels when the payer block collected it OR the house
          // required it — typing into a labelled required field is the consent,
          // the same argument the payer block makes.
          guest_phone: (needsPayer || phoneRequired) && cleanPhone ? cleanPhone : null,
          contact_consent:
            (needsPayer && !!(email.trim() || cleanPhone)) || (phoneRequired && !!cleanPhone),
          /*
           * THE TICK TRAVELS, because a checkbox in a React tree is not a
           * record of anything. The server refuses the order without it where
           * the house has terms, and stamps `terms_accepted_at` on the row
           * where it has them — so the acceptance the acquirer asks about
           * exists as a fact in the database rather than as a component that
           * once rendered on somebody's phone.
           */
          terms_accepted: termsOk,
          // The phone's Telegram key (0035), when Start was ever pressed on
          // this browser — the server attaches the new order to the chat and
          // the bot confirms it, which is why no invitation is drawn again.
          tg_key: localStorage.getItem("prispa.tg.key") ?? undefined,
          tip_minor: tip,
          payment_method: method,
          /*
           * „O IAU EU LA BAR." The flag asks the server to read the staff
           * cookie this request is already carrying; the cookie is what
           * actually decides. See `barActor` in worker/api/orders.ts — an
           * unauthenticated caller sending this gets a 401 and nothing else.
           */
          bar_order: bar,
          lang: props.lang,
          idempotency_key: props.idempotencyKey,
          items: props.lines.map((l) => ({
            menu_item_id: l.menu_item_id,
            qty: l.qty,
            upsell_rule_id: l.upsell_rule_id ?? null,
          })),
        }),
      });

      const body = (await res.json()) as {
        order_id?: string; pay_url?: string | null;
        error?: string; shortfalls?: { menu_item_id: string }[];
        /**
         * Present on `payment_unavailable`, and only outside production —
         * worker/api/orders.ts populates it EXPRESSLY so a sandbox can be
         * diagnosed from the screen it broke on, then withholds it in
         * production because an upstream message can quote our own request
         * back at us and a guest's phone is not where that belongs.
         */
        detail?: string;
        /** Sent on `payment_unavailable`: the order exists even though the bank refused. */
        pickup_code?: string;
        /**
         * On a clean 201. Mod bar reads both — the code to say aloud and the
         * cash to collect — because a counter order goes nowhere afterwards
         * and this response is the only place they appear.
         */
        total_minor?: number;
      };

      if (!res.ok) {
        /*
         * "Mai încearcă o dată" WAS THE ANSWER TO EVERY REFUSAL, and for three
         * of them it is a lie the guest cannot get out of.
         *
         * The order route refuses with `event_closed` and `event_over` once the
         * evening has ended — and it carries a ready-made Romanian `detail`
         * saying so, which was thrown away. It refuses with `item_unavailable`
         * when something has passed its serving window. Telling somebody to try
         * again is telling them to keep tapping a button that will never work,
         * at a table, with a phone in their hand and nobody to ask.
         */
        if (body.error === "insufficient_stock" && body.shortfalls) {
          const names = body.shortfalls
            .map((s) => props.lines.find((l) => l.menu_item_id === s.menu_item_id)?.name)
            .filter(Boolean).join(", ");
          setError(`${t.soldOut} ${names}`);
        } else if (body.error === "payment_unavailable" && body.order_id) {
          /*
           * NOT A MESSAGE — A MOVE. The order exists and the server has just
           * marked it `failed` (worker/api/orders.ts), which is the one state
           * `POST /orders/:id/pay` accepts. Everything the guest needs next is
           * already built on their own order screen: MIA, card, and "Plătesc la
           * bar", which converts the order to cash so the counter can settle it.
           *
           * `onPlaced` is the same handler a SUCCESSFUL order uses, and it has
           * to be: it stores the session that proves the order is theirs —
           * without which that screen shows no totals and no retry at all —
           * records where "Înapoi la meniu" goes, and clears the basket, since
           * the order is the record now. Passing `null` for the pay URL is what
           * sends them to /o/<id> rather than to a bank page that does not
           * exist.
           *
           * Leaving them on the checkout with a sentence was the alternative,
           * and it is a dead end: this screen's only button retries the request
           * that just failed.
           */
          // A placed order, even though the bank refused a page for it — the
          // row and the reservation exist, so the choices were real and are
          // worth remembering exactly as a clean success is.
          rememberHabits();
          // The draft described an order that does not exist yet. It does now.
          clearDraft(props.eventSlug);
          props.onPlaced(body.order_id, null, method);
          return;
        } else if (body.error === "payment_unavailable") {
          /*
           * THE FOURTH REFUSAL THE COMMENT ABOVE IS ABOUT, and it was the one
           * still landing in "mai încearcă o dată".
           *
           * The bank would not open a payment page. Retrying cannot mend that,
           * and the order EXISTS — worker/api/orders.ts keeps the row and the
           * stock reservation on purpose, with a real number and pickup code —
           * so the guest is not being asked to start again, they are being
           * pointed at the counter.
           *
           * `detail` is appended when the server sent one, which it does
           * everywhere except production. It is how a sandbox says "empty 403
           * from maib" on the screen it happened on, instead of costing an
           * afternoon of `wrangler tail`.
           */
          setError(
            [
              // The method the guest actually chose — see payDownMia.
              method === "mia" ? t.payDownMia : t.payDown,
              body.pickup_code,
              // Outside production only. See the field's note above.
              body.detail ? `(${body.detail})` : null,
            ].filter(Boolean).join(" ")
          );
        } else if (body.error === "terms_not_accepted") {
          /*
           * THE SERVER'S OWN GATE, ANSWERED IN WORDS. Unreachable from this
           * screen in a working browser — the bar is dead and `place()` refuses
           * before the fetch — but it IS reachable when the house publishes
           * terms while a checkout sits open on an older snapshot, and „nu am
           * putut trimite comanda, mai încearcă" would then be a loop with no
           * exit. This names the box instead, which by then has been drawn.
           */
          setError(t.termsRequired);
        } else if (
          body.error === "staff_auth_required" ||
          body.error === "forbidden" ||
          body.error === "bad_origin" ||
          body.error === "bar_order_cash_only"
        ) {
          /*
           * MOD BAR, REFUSED BY THE SERVER — the credential is missing,
           * expired, revoked, belongs to another house, or holds no
           * `order.take_payment`. Reachable in ordinary use: a device token
           * dies at its event's end (worker/lib/identity.ts) and a tablet left
           * on all night will meet exactly this. „Mai încearcă o dată" would
           * be a loop; signing in again is the actual way out.
           */
          setError(t.barDenied);
        } else if (body.error === "event_closed" || body.error === "event_over") {
          setError(t.over);
        } else if (
          body.error === "item_not_yet_available" ||
          body.error === "item_no_longer_available" ||
          body.error === "item_not_on_menu" ||
          body.error === "menu_not_published"
        ) {
          setError(t.unavailable);
        } else {
          setError(t.error);
        }
        return;
      }
      /*
       * THE HABITS ARE LEARNED HERE AND NOWHERE EARLIER.
       *
       * Not on the button tap: a guest who tries 15%, sees the total, and
       * abandons the basket has chosen nothing. An order that reached the
       * server is the only evidence of what they actually meant.
       */
      rememberHabits();
      // The habits are kept; the half-finished form is not — it re-opens from
      // the habits next time, which is the same thing said once. `bar` wrote
      // no draft, and the one sitting there belongs to a guest of this house
      // who is mid-order on their own phone: deleting it is not this screen's
      // to do.
      if (!bar) clearDraft(props.eventSlug);
      /*
       * A COUNTER ORDER DOES NOT TRAVEL. `onPlaced` navigates — to a bank page
       * or to /o/<id> — and both are wrong here: the staff need the code and
       * the amount for the person in front of them, and then the menu back for
       * the next one. A cash order has no `pay_url` to go to anyway.
       */
      if (bar) {
        props.onBarPlaced?.(body.pickup_code ?? "", body.total_minor ?? total);
        return;
      }
      props.onPlaced(body.order_id!, body.pay_url ?? null, method);
    } catch {
      setError(t.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-linen">
      <div className="mx-auto max-w-lg px-5 pb-36 pt-6">
        <button onClick={props.onClose} className="-ml-2 flex items-center px-2 text-ink-muted" style={{ minHeight: 44 }}>
          ← {t.back}
        </button>

        <h1 className="mt-3" style={{ fontSize: "var(--text-2xl)" }}>{t.title}</h1>

        {props.lines.length === 0 ? (
          <p className="mt-6 text-ink-muted">{t.empty}</p>
        ) : (
          <>
            <ul className="m-0 mt-5 list-none border-t border-dove-200 p-0">
              {props.lines.map((l) => (
                <li key={l.menu_item_id} className="flex items-start gap-3 border-b border-dove-100 py-3">
                  {/* Same picture as on the menu, so the cart is recognisable
                      at a glance rather than a list of words. */}
                  <img
                    src={`/api/img/${l.product_id}`} alt="" width={52} height={52} loading="lazy"
                    className="shrink-0 border border-dove-200 bg-paper"
                    style={{ width: 52, height: 52, objectFit: "cover" }}
                  />
                  {/*
                    * TWO LINES, THE SAME SHAPE THE MENU ROW USES.
                    *
                    * This was one flex row — image, name, stepper, line total —
                    * and it stopped lining up the moment "L" became "MDL". The
                    * total is the LAST thing in the row and the stepper is
                    * `shrink-0` before it, so every row's stepper sat wherever
                    * that row's total happened to end: 135,00 MDL is wider than
                    * 50,00 MDL, so the steppers stepped. Pinning the total back
                    * to a fixed width is what caused the original overflow, and
                    * on a 390px phone there is no width that fits
                    * "1.234,50 MDL" and still leaves room for a name.
                    *
                    * So the row stacks the way the menu's rows already do: name
                    * and line total on the first line, unit price and stepper on
                    * the second. Both right-hand things are flush to the same
                    * edge, so both columns line up down the list by
                    * construction, and the name gets the width it needs.
                    */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="min-w-0" style={{ fontSize: "var(--text-base)" }}>{l.name}</p>
                      <span className="tabular shrink-0 whitespace-nowrap" style={{ fontSize: "var(--text-base)" }}>
                        {lei(l.price_minor * l.qty)}
                      </span>
                    </div>
                    {pendingRemove === l.menu_item_id ? (
                      /*
                        * THE QUESTION TAKES THE STEPPER'S PLACE, in the row it
                        * is about — no overlay, no dialog, and the name it
                        * refers to is the line above it. „Rămâne" restores the
                        * row exactly as it was; nothing has changed until
                        * „Da, scoate" says so.
                        */
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <span className="min-w-0 text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
                          {t.removeAsk}
                        </span>
                        <div className="flex shrink-0 items-center gap-2">
                          <button
                            onClick={() => setPendingRemove(null)}
                            className="border border-dove-400 bg-paper px-4 text-ink-muted"
                            style={{ borderRadius: 0, minHeight: 44 }}
                          >
                            {t.removeNo}
                          </button>
                          <button
                            onClick={() => { setPendingRemove(null); props.onQty(l.menu_item_id, 0); }}
                            className="border px-4"
                            style={{
                              borderRadius: 0, minHeight: 44,
                              color: "var(--color-danger)", borderColor: "var(--color-danger)",
                            }}
                          >
                            {t.removeYes}
                          </button>
                        </div>
                      </div>
                    ) : (
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className="tabular text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                        {lei(l.price_minor)}
                      </span>
                      <div className="flex shrink-0 items-center gap-2">
                        <div className="flex shrink-0 items-center border border-dove-400" role="group" aria-label={l.name}>
                          {/* Minus from 1 would empty the line, so it asks the
                              same question the bin does instead of acting. */}
                          <button
                            onClick={() =>
                              l.qty <= 1
                                ? setPendingRemove(l.menu_item_id)
                                : props.onQty(l.menu_item_id, l.qty - 1)
                            }
                            aria-label={`− ${l.name}`}
                            style={{ minHeight: 44, minWidth: 44, borderRadius: 0 }}
                          >
                            −
                          </button>
                          <span className="tabular w-7 text-center" aria-live="polite">{l.qty}</span>
                          <button onClick={() => props.onQty(l.menu_item_id, l.qty + 1)} aria-label={`+ ${l.name}`}
                            style={{ minHeight: 44, minWidth: 44, borderRadius: 0 }}>+</button>
                        </div>
                        {/* The explicit way out, LAST in the row — the same
                            edge the line total sits on, so destructive and
                            additive never share a corner. Same destination as
                            minus from 1, same question first. */}
                        <button
                          onClick={() => setPendingRemove(l.menu_item_id)}
                          aria-label={`${t.remove} ${l.name}`}
                          className="flex items-center justify-center border border-dove-400 bg-paper text-ink-faint"
                          style={{ minHeight: 44, minWidth: 44, borderRadius: 0 }}
                        >
                          <RemoveMark />
                        </button>
                      </div>
                    </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* HIDDEN, NOT MERELY OPTIONAL.
                Making only the VALIDATION conditional left the field on screen,
                so a house that chose "doar codul" still put a keyboard in front
                of every guest — the setting looked broken because on the one
                screen it exists for, nothing changed. */}
            {/*
              * AT THE COUNTER THE BOX IS ALWAYS THERE, AND NEVER REQUIRED.
              *
              * A house that hands over by pickup code alone draws no name box,
              * and for a guest that is right — the code is on the card in
              * their hand. At the counter the code is on nobody's card until
              * somebody writes it down, and „Maria" shouted with it is what
              * actually gets a tray to an eighty-year-old who has sat down
              * somewhere. Optional either way: `place()` waives the
              * requirement here, exactly as the server does.
              */}
            {(nameRequired || bar) && (
              <>
                <label htmlFor="g-name" className="mt-7 block text-ink-muted">{t.name}</label>
                <input
                  id="g-name" ref={nameRef} value={name}
                  onChange={(e) => { setName(e.target.value); clearRefusal(); }}
                  placeholder={t.namePh}
                  className="mt-2 w-full border border-dove-400 bg-paper px-4 text-ink outline-none focus:border-clay-600"
                  style={{ borderRadius: 0, minHeight: 56, fontSize: "var(--text-base)", ...flagged("name") }}
                />
              </>
            )}

            {/* A house where the person carrying the tray is the owner's
                daughter, at an evening sold per person, does not want a machine
                asking her guests for more money. D-02 made tipping optional and
                it was built always-on. */}
            {askForTip && (<>
            <p className="mt-6 text-ink-muted">{t.tip}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {offeredPercents.map((v) => {
                const on = !customOpen && tipPercent === v;
                return (
                  <button
                    key={v}
                    onClick={() => { setCustomOpen(false); setTipPercent(v); }}
                    aria-pressed={on}
                    className={on ? "bg-olive-500 px-5 text-paper" : "border border-dove-400 bg-paper px-5 text-ink-muted"}
                    style={{ borderRadius: 0, minHeight: 52 }}
                  >
                    {v === 0 ? t.tipNone : `${v}%`}
                  </button>
                );
              })}
              <button
                onClick={() => { setCustomOpen(true); setTipPercent(0); }}
                aria-pressed={customOpen}
                className={customOpen ? "bg-olive-500 px-5 text-paper" : "border border-dove-400 bg-paper px-5 text-ink-muted"}
                style={{ borderRadius: 0, minHeight: 52 }}
              >
                {t.tipOther}
              </button>
            </div>

            {customOpen && (
              <div className="mt-3 flex items-center gap-3">
                <input
                  inputMode="numeric"
                  autoFocus
                  value={customPercent}
                  onChange={(e) => {
                    // Clamped AT THE KEY, not refused after: 1001 becomes 1000
                    // as it is typed, so the ceiling explains itself — the old
                    // behaviour parsed anything over the cap to nothing, and
                    // the guest watched their tip silently turn into „Fără".
                    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 4);
                    setCustomPercent(digits !== "" && Number(digits) > 1000 ? "1000" : digits);
                  }}
                  placeholder={t.tipOtherPh}
                  aria-label={t.tipOther}
                  className="tabular w-24 border border-dove-400 bg-paper px-4 text-center text-ink outline-none focus:border-clay-600"
                  style={{ borderRadius: 0, minHeight: 52, fontSize: "var(--text-lg)" }}
                />
                <span className="text-ink-muted" style={{ fontSize: "var(--text-lg)" }}>%</span>
              </div>
            )}

            {/* What the percentage actually comes to, in money, before they
                commit to it. The percentage is the input; the sum is the fact.
                The face beside it is ALWAYS drawn — the quietly sad one on
                „Fără" is the entire nudge (see tipFace) — while the numbers
                appear only once there is a sum to state. */}
            <p className="mt-2 flex items-center gap-2 text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
              <span
                aria-hidden="true"
                // Re-keyed per face so the swap re-runs the pop animation —
                // the little jump is what makes the reaction feel like one.
                key={tipFace(effectivePercent)}
                className="tip-face"
                style={{ fontSize: "var(--text-xl)", lineHeight: 1 }}
              >
                {tipFace(effectivePercent)}
              </span>
              {tip > 0 && <span>{effectivePercent}% · {lei(tip)}</span>}
            </p>

            {/* Optional, and hidden until asked for. Ticking it IS the consent —
                the field does not exist until you do. */}
            </>)}

            {/*
              * NO PICKER AT THE COUNTER — the method is stated, not offered.
              *
              * Three buttons with one of them permanently lit is a choice that
              * is not a choice, and on a busy tablet it is a thing to tap by
              * accident. The line below says what will happen instead, in the
              * same place the question used to be, so the screen still reads
              * top-down and still answers „cum plătesc".
              */}
            {bar ? (
              <p className="mt-6 text-ink-muted">
                {t.method}: <span className="text-ink" style={{ fontFamily: "var(--font-display)" }}>{t.cash}</span>
              </p>
            ) : (<>
            <p className="mt-6 text-ink-muted">{t.method}</p>
            <div className="mt-2 grid gap-2">
              {/* Only what the house can actually be paid by, intersected with
                  what this deployment can complete — see deploymentCanTake in
                  worker/payments. A button that leads to a page the server
                  refuses is worse than no button. */}
              {/* Card leads — the order matches the preselection, so the lit
                  button is the top one and the screen reads top-down. */}
              {([["card", t.card, ""], ["mia", t.mia, t.miaHint], ["cash", t.cash, ""]] as const)
                .filter(([id]) => methods.includes(id))
                .map(([id, label, hint]) => (
                <button
                  key={id} onClick={() => setMethod(id)} aria-pressed={method === id}
                  className={`px-5 text-left ${method === id ? "border-2 border-clay-500 bg-clay-50" : "border border-dove-400 bg-paper text-ink-muted"}`}
                  style={{ borderRadius: 0, minHeight: 56 }}
                >
                  <span style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-base)" }}>{label}</span>
                  {hint && <span className="ml-2 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>{hint}</span>}
                </button>
              ))}
            </div>
            </>)}

            {/*
              * WHO IS PAYING — under the choice that decides whether to ask.
              *
              * The block used to sit ABOVE the payment buttons, which put the
              * question before its cause: a guest read „Datele plătitorului"
              * without having yet said they were paying online at all. Owner's
              * call, 2026-08-12 — the method chooses, then the method's
              * consequences follow, and the block is the SAME for card and MIA
              * (see `needsPayer`).
              *
              * maib's `payerInfo` takes a name, an email and a phone, and
              * NOTHING ELSE: there is no address, city, country or postcode
              * anywhere in `PayerDto`. So those are not asked for. Collecting a
              * billing address we could never send would be gathering personal
              * data for no purpose, which is the one thing this app has been
              * careful not to do.
              *
              * NAME AND PHONE STAY OPTIONAL, because maib makes them optional:
              * the v2 contract requires `amount` and `currency` and nothing
              * else, and refusing an order for a missing phone number would be
              * inventing a rule the bank does not have. THE EMAIL IS THE
              * EXCEPTION, and not because of `payerInfo`: maib's certification
              * requires a confirmation email to every customer, so the address
              * is required for any online payment — see `needsEmail`, and the
              * hint under the box that says where it goes.
              */}
            {needsPayer && (
              <div className="mt-6">
                <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>
                  {t.payer}
                </p>
                <p className="mt-1 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                  {t.payerHint}
                </p>
                {/*
                  * THE NAME BELONGS HERE WHEN NOTHING ELSE ASKED FOR IT.
                  *
                  * The name question is drawn only where the house has
                  * `require_guest_name` on. A house that hands over by pickup
                  * code alone — the common case, and the one this event uses —
                  * renders no name box at all, so `guest_name` falls back to
                  * the ZONE LABEL and `payerFor` then drops it, correctly,
                  * because a table is not a cardholder.
                  *
                  * The result was a payer block that asked for an email and a
                  * phone and never once obtained a name. Same `name` state, so
                  * a house that DOES ask still gets exactly one box.
                  */}
                {!nameRequired && (
                  <input
                    value={name} onChange={(e) => { setName(e.target.value); setError(null); }}
                    autoComplete="name" placeholder={t.payerNamePh}
                    className="mt-3 w-full border border-dove-400 bg-paper px-4 text-ink outline-none focus:border-clay-600"
                    style={{ borderRadius: 0, minHeight: 56, fontSize: "var(--text-base)" }}
                  />
                )}
                <input
                  type="email" inputMode="email" autoComplete="email" value={email} ref={emailRef}
                  onChange={(e) => { setEmail(e.target.value); clearRefusal(); }}
                  placeholder={t.payerEmailPh}
                  className="mt-2 w-full border border-dove-400 bg-paper px-4 text-ink outline-none focus:border-clay-600"
                  style={{ borderRadius: 0, minHeight: 56, fontSize: "var(--text-base)", ...flagged("email") }}
                />
                <p className="mt-1 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                  {t.emailConfirmHint}
                </p>
                <input
                  type="tel" inputMode="tel" autoComplete="tel" value={phone} ref={phoneRef}
                  onChange={(e) => { setPhone(e.target.value); clearRefusal(); }}
                  placeholder={t.payerPhonePh}
                  className="mt-2 w-full border border-dove-400 bg-paper px-4 text-ink outline-none focus:border-clay-600"
                  style={{ borderRadius: 0, minHeight: 56, fontSize: "var(--text-base)", ...flagged("phone") }}
                />
              </div>
            )}

            {/*
              * WHAT CASH MUST STILL LEAVE BEHIND, where the house asked for it
              * (v8). Not the payer block: nothing here goes to a bank, and
              * saying it would is the kind of hint that gets a form abandoned.
              * The email doubles as the receipt address — that is its whole
              * purpose on a cash order — so the opt-in tick below never draws
              * beside it.
              */}
            {!needsPayer && (contactWanted.email || phoneRequired) && (
              <div className="mt-6">
                <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>
                  {t.contactTitle}
                </p>
                {contactWanted.email && (
                  <>
                    <input
                      type="email" inputMode="email" autoComplete="email" value={email} ref={emailRef}
                      onChange={(e) => { setEmail(e.target.value); clearRefusal(); }}
                      placeholder={t.emailPh}
                      className="mt-3 w-full border border-dove-400 bg-paper px-4 text-ink outline-none focus:border-clay-600"
                      style={{ borderRadius: 0, minHeight: 56, fontSize: "var(--text-base)", ...flagged("email") }}
                    />
                    <p className="mt-1 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                      {t.cashEmailHint}
                    </p>
                  </>
                )}
                {phoneRequired && (
                  <input
                    type="tel" inputMode="tel" autoComplete="tel" value={phone} ref={phoneRef}
                    onChange={(e) => { setPhone(e.target.value); clearRefusal(); }}
                    placeholder={t.payerPhonePh}
                    className="mt-2 w-full border border-dove-400 bg-paper px-4 text-ink outline-none focus:border-clay-600"
                    style={{ borderRadius: 0, minHeight: 56, fontSize: "var(--text-base)", ...flagged("phone") }}
                  />
                )}
              </div>
            )}

            {/*
              * THE OPT-IN SURVIVES ONLY WHERE IT IS STILL A CHOICE. An online
              * payment sends the confirmation unconditionally — the bank's
              * certification requires it — so drawing a tick there would offer
              * a decision the guest does not have. Cash keeps it exactly as it
              * was: no bank, no requirement, the guest decides. Under the
              * method buttons for the same reason the payer block is: it is a
              * consequence of choosing cash.
              */}
            {/*
              * AND NEVER AT THE COUNTER. The tick asks the guest for an address
              * to send a bon to, and here the guest is not the one reading the
              * screen — see the note beside `contactWanted`. The house can
              * print at the counter; nothing is lost but a box staff would
              * have to skip fifty times an evening.
              */}
            {!needsEmail && !bar && (<>
            <label className="mt-6 flex items-start gap-3">
              <input
                type="checkbox" checked={wantReceipt}
                onChange={(e) => { setWantReceipt(e.target.checked); setError(null); }}
                className="mt-0.5 h-6 w-6 shrink-0 accent-[var(--color-olive-500)]"
              />
              <span className="text-ink-muted">{t.receipt}</span>
            </label>
            {wantReceipt && (
              <input
                type="email" inputMode="email" autoComplete="email" value={email} ref={emailRef}
                onChange={(e) => { setEmail(e.target.value); clearRefusal(); }} placeholder={t.emailPh}
                className="mt-2 w-full border border-dove-400 bg-paper px-4 text-ink outline-none focus:border-clay-600"
                style={{ borderRadius: 0, minHeight: 56, fontSize: "var(--text-base)", ...flagged("email") }}
              />
            )}
            </>)}

            {/*
              * THE ONE BOX THAT BLOCKS THE ORDER — directly under the payer
              * details, so the last thing before the bill is the agreement
              * about it.
              *
              * Drawn only where the house has published terms AND the payment
              * is online — cash is exempt (owner's call, 2026-08-12): the
              * acceptance exists because the acquirer requires it before money
              * moves online, and a guest paying at the counter is handed their
              * contract by a person. Never pre-ticked. The bottom bar is
              * disabled until it is ticked, and `place()` refuses as well.
              *
              * NOT A <label> AROUND THE WHOLE SENTENCE, unlike the receipt box
              * three lines up, and the difference is the link. A label
              * activates its control on any click inside it, so an <a> nested
              * in one both follows the link and toggles the tick — a guest who
              * taps „termenii și condițiile" to read them would come back to a
              * box that had silently changed state, in either direction. So the
              * label wraps only the words before the link, and the anchor sits
              * outside it. Both are still comfortably tappable.
              */}
            {termsUrl && needsPayer && (
              <div className="mt-6 flex items-start gap-3">
                <input
                  id="g-terms"
                  ref={termsRef}
                  type="checkbox"
                  checked={termsOk}
                  onChange={(e) => { setTermsOk(e.target.checked); clearRefusal(); }}
                  className="mt-0.5 h-6 w-6 shrink-0 accent-[var(--color-olive-500)]"
                />
                {/* The sentence wears the danger colour while the refusal
                    points here — a 24px box alone is easy to scroll past. */}
                <span style={errorField === "terms" ? { color: "var(--color-danger)" } : undefined} className="text-ink-muted">
                  <label htmlFor="g-terms">{t.terms} </label>
                  <a
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-clay-600 underline"
                  >
                    {t.termsLink}
                  </a>
                </span>
              </div>
            )}

            <div className="mt-7 border-t border-dove-200 pt-4">
              <div className="flex justify-between text-ink-muted">
                <span>{t.subtotal}</span><span className="tabular">{lei(props.subtotal)}</span>
              </div>
              {tip > 0 && (
                <div className="mt-1 flex justify-between text-ink-muted">
                  <span>{t.tipLine}</span><span className="tabular">{lei(tip)}</span>
                </div>
              )}
              <div className="mt-2 flex justify-between" style={{ fontSize: "var(--text-xl)" }}>
                <span>{t.total}</span><span className="tabular">{lei(total)}</span>
              </div>
            </div>

          </>
        )}
      </div>

      {props.lines.length > 0 && (
        /* Whole strip, same as the menu's bar — see the long note there. The
           padding used to belong to a paper wrapper, so twelve pixels of what
           looked like button did nothing. No chevron here: this bar takes
           money, and it carries the banknote instead, so the two bars on
           consecutive screens do not read as the same button. */
        /*
         * GREY UNTIL THE TERMS ARE ACCEPTED — BUT NEVER DEAD. Owner's ask,
         * 2026-08-16, and it is the checkout finally obeying the product's own
         * rule (BRIEF-shopping §126, quoted in Menu.tsx): "a greyed-out
         * control invites the tap that proves it is dead" — an inert control
         * must be EXPLAINED, never silently grey. This one was `disabled`, so
         * guests tapped the grey bar, nothing whatsoever happened, and the
         * sentence that would have named the unticked box sat unreachable
         * behind the very attribute meant to communicate it.
         *
         * So the bar keeps the grey — the "not ready yet" reading is right and
         * stays — but the tap now lands: `place()` refuses before any fetch,
         * with the house's full refusal (the sentence, the scroll to the box,
         * the danger colour on it), which is exactly what the guest needed to
         * be told. `aria-disabled` keeps saying "not actionable yet" to a
         * screen reader, and `aria-describedby` still points at the box that
         * would revive it.
         *
         * STILL TRULY DISABLED while busy or while the house's rules are
         * unknown. See `rulesKnown` above: `termsUrl` is "" both for a house
         * with no terms and for a snapshot that has not arrived, and during
         * that window there is no box to walk anybody to — a tap there has no
         * honest answer, so it stays swallowed.
         */
        <button
          onClick={place} disabled={busy || !rulesKnown}
          aria-disabled={busy || !rulesKnown || (!!termsUrl && needsPayer && !termsOk)}
          aria-describedby={termsUrl && needsPayer && !termsOk ? "g-terms" : undefined}
          aria-label={`${busy ? t.placing : bar ? t.barPlace : method === "cash" ? t.place : t.payNow} — ${lei(total)}`}
          className={`fixed inset-x-0 bottom-0 z-30 flex w-full items-center justify-center px-4 text-paper disabled:bg-dove-400 ${
            !!termsUrl && needsPayer && !termsOk ? "bg-dove-400" : "bg-clay-500"
          }`}
          style={{
            borderRadius: 0,
            fontFamily: "var(--font-display)",
            fontSize: "var(--text-lg)",
            minHeight: 64,
            paddingTop: 12,
            paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <span className="flex w-full max-w-lg items-center justify-between gap-4">
            {/* The mark comes BEFORE the word, so the thing about to happen is
                read before the thing being done. */}
            <span className="flex min-w-0 items-center gap-3">
              <PaymentMark />
              {/*
                * "Plătește acum", not "Comandă" — EXCEPT FOR CASH.
                *
                * This is the last screen before the money moves, and on the MIA
                * and card rails the very next thing is the bank's own page; the
                * button should say what it is about to do, in the same three
                * words the order screen uses for the same action (see
                * OrderStatus's `pay`), so a guest who lands there after a
                * failure recognises it.
                *
                * Cash keeps "Comandă". Nothing is paid now: the guest walks to
                * the bar with a code and settles there. A button promising
                * payment that then takes none is a small lie somebody at the
                * counter has to explain.
                */}
              <span className="truncate">
                {busy ? t.placing : bar ? t.barPlace : method === "cash" ? t.place : t.payNow}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-3">
              <span className="tabular">{lei(total)}</span>
              {method !== "cash" && <GoMark />}
            </span>
          </span>
        </button>
      )}

      {/*
        * THE REFUSAL, IN FRONT OF THE TAP THAT CAUSED IT.
        *
        * It was a paragraph above the totals, and the totals sit half a screen
        * above the bar that was tapped — on a long checkout the sentence
        * landed off-screen, so the tap appeared to do nothing at all: the
        * exact dead end the old role="alert" note warned about for screen
        * readers, delivered to everyone. Now it is the same bottom sheet every
        * other piece of news on these screens uses (the ready pop-up, the
        * pairing suggestion), in the danger colour, OVER the pay bar it
        * answers — z-40 to the bar's z-30, so the next tap must be „Am
        * înțeles" rather than the same doomed retry.
        *
        * role="alert" stays, and stays assertive: it only ever fires in answer
        * to the guest's own tap. Typing into any field clears it, as before.
        */}
      {error && (
        <div
          role="alert"
          className="fixed inset-x-0 bottom-0 z-40 border-t-2 bg-paper p-5 shadow-[0_-2px_40px_-10px_rgba(83,54,36,0.25)]"
          style={{
            borderTopColor: "var(--color-danger)",
            paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <div className="mx-auto max-w-lg">
            <p style={{ color: "var(--color-danger)" }}>{error}</p>
            <button
              /*
                * „Am înțeles" is not only dismissal — it is "show me": the
                * cursor lands in the field the sentence was about, already
                * scrolled to the middle of the screen by `refuse`, still
                * wearing the danger border until it is answered.
                */
              onClick={() => { setError(null); fieldRef(errorField)?.current?.focus(); }}
              className="tap mt-4 w-full border border-dove-400 bg-paper px-5 text-ink-muted"
              style={{ borderRadius: 0, minHeight: 52 }}
            >
              {t.errOk}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
